import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Star, Pencil, Trash2, Check, X, Users, FileText, Layers } from 'lucide-react';
import { api, type ApiError } from '../../api';
import type { Year } from '../Manage';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

export default function ManageYears({ years, reload }: { years: Year[]; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [year, setYear] = useState('');
  const [cloneFrom, setCloneFrom] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editYear, setEditYear] = useState('');
  const [roundFrom, setRoundFrom] = useState<number | null>(null);
  const [roundCount, setRoundCount] = useState('5');
  const [roundType, setRoundType] = useState<'poster' | 'oral'>('poster');
  const editRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editId !== null || roundFrom !== null) editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editId, roundFrom]);

  async function act(body: Record<string, unknown>, ok: string) {
    setBusy(true);
    try {
      await api('admin-years', { body });
      toast.success(ok);
      await reload();
      return true;
    } catch (e) {
      toast.error((e as ApiError).message || 'Something went wrong');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    if (!label.trim()) return toast.error('Give the year a name');
    if (await act({ action: 'create', label: label.trim(), year, cloneJudgesFrom: cloneFrom || null }, cloneFrom ? 'Year created with judges copied across' : 'Year created')) {
      setLabel('');
      setYear('');
      setCloneFrom('');
    }
  }

  async function createRound() {
    if (roundFrom == null) return;
    setBusy(true);
    try {
      const r = await api<{ year: { label: string }; copied: number }>('admin-years', {
        body: { action: 'createRound', fromYearId: roundFrom, count: Number(roundCount) || 5, type: roundType },
      });
      toast.success(`${r.year.label} created — ${r.copied} ${roundType}${r.copied === 1 ? '' : 's'} copied`);
      setRoundFrom(null);
      await reload();
    } catch (e) {
      toast.error((e as ApiError).message || 'Could not create the round');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      {roundFrom !== null ? (
        <Card ref={editRef} className="scroll-mt-20 space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-semibold">
              <Layers className="size-4 text-primary" /> New round
            </h2>
            <Button size="icon" variant="ghost" onClick={() => setRoundFrom(null)} aria-label="Cancel"><X /></Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Creates a linked round from{' '}
            <span className="font-medium text-foreground">{years.find((y) => y.id === roundFrom)?.label}</span>: copies
            its judges and rubrics plus the top-scoring presentations of the chosen type. Allocations are not copied —
            set those up in the new round.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="r-count">Top</Label>
              <Input id="r-count" type="number" min={1} value={roundCount} onChange={(e) => setRoundCount(e.target.value)} className="w-20" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="r-type">Type</Label>
              <select
                id="r-type"
                value={roundType}
                onChange={(e) => setRoundType(e.target.value as 'poster' | 'oral')}
                className="h-11 rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="poster">Posters</option>
                <option value="oral">Orals</option>
              </select>
            </div>
            <Button onClick={createRound} disabled={busy}><Layers /> Create round</Button>
            <Button variant="ghost" onClick={() => setRoundFrom(null)}>Cancel</Button>
          </div>
        </Card>
      ) : editId !== null ? (
        <Card ref={editRef} className="scroll-mt-20 space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-semibold">
              <Pencil className="size-4 text-primary" /> Edit year
            </h2>
            <Button size="icon" variant="ghost" onClick={() => setEditId(null)} aria-label="Cancel"><X /></Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
            <div className="space-y-1.5">
              <Label htmlFor="e-label">Name</Label>
              <Input id="e-label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="e-year">Year</Label>
              <Input id="e-year" type="number" value={editYear} onChange={(e) => setEditYear(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={async () => { if (await act({ action: 'rename', yearId: editId, label: editLabel.trim(), year: editYear }, 'Renamed')) setEditId(null); }} disabled={busy}>
              <Check /> Save changes
            </Button>
            <Button variant="ghost" onClick={() => setEditId(null)}>Cancel</Button>
          </div>
        </Card>
      ) : (
      <Card className="space-y-4 p-5">
        <h2 className="flex items-center gap-2 font-semibold">
          <Plus className="size-4 text-primary" /> New year
        </h2>
        <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
          <div className="space-y-1.5">
            <Label htmlFor="y-label">Name</Label>
            <Input id="y-label" placeholder="AADPA 2026" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="y-year">Year</Label>
            <Input id="y-year" type="number" placeholder="2026" value={year} onChange={(e) => setYear(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="y-clone">Copy judges from</Label>
          <select
            id="y-clone"
            value={cloneFrom}
            onChange={(e) => setCloneFrom(e.target.value)}
            className="flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Start with no judges</option>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.label} ({y.judge_count} judges)
              </option>
            ))}
          </select>
        </div>
        <Button onClick={create} disabled={busy}>
          <Plus /> Create year
        </Button>
      </Card>
      )}

      <div className="space-y-3">
        {years.map((y) => (
          <Card key={y.id} className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{y.label}</span>
                    {y.is_current && (
                      <Badge variant="success">
                        <Star className="size-3" /> Current
                      </Badge>
                    )}
                    {y.round_no ? <Badge variant="secondary">Round {y.round_no}</Badge> : null}
                  </div>
                  <div className="mt-0.5 flex gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Users className="size-3.5" /> {y.judge_count} judges
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <FileText className="size-3.5" /> {y.presentation_count} presentations
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {!y.is_current && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (
                          (y.judge_count === 0 || y.presentation_count === 0) &&
                          !confirm(
                            `${y.label} has ${y.judge_count} judges and ${y.presentation_count} presentations. Judges won't be able to sign in or score until it has both. Make it the live year anyway?`,
                          )
                        )
                          return;
                        act({ action: 'setCurrent', yearId: y.id }, `${y.label} is now the live year`);
                      }}
                      disabled={busy}
                    >
                      <Star /> Set current
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setEditId(null); setRoundFrom(y.id); setRoundCount('5'); setRoundType('poster'); }}>
                    <Layers /> Round
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditId(y.id); setEditLabel(y.label); setEditYear(y.year?.toString() ?? ''); }} aria-label="Rename">
                    <Pencil />
                  </Button>
                  {!y.is_current && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      aria-label="Delete"
                      onClick={() => { if (confirm(`Delete "${y.label}"? This permanently removes its ${y.judge_count} judges, ${y.presentation_count} presentations and all their scores.`)) act({ action: 'delete', yearId: y.id }, 'Year deleted'); }}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
